import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { defineMessages, FormattedMessage } from 'react-intl';
import OkCancelModal from '@/shared/components/OkCancelModal';
import { listItemMsg } from '@/shared/components/FormattedListValue';
import api from '@/shared/api';
import config from '@/config';
import log from 'loglevel';
import intlShape from '@/shared/utils/intlShape';
import ToastrService from '@/shared/services/ToastrService';
import CollapsablePanel from '@/shared/components/CollapablePanel';
import EmailEditor from '@/shared/components/EmailEditor';
import { injectStateService } from '@/shared/services/StateService';
import { LinkIcon } from '@/shared/components/Icons';
import moment from 'moment';
import Button from '@/shared/components/Button';
import CanRead from '@/shared/components/security/CanRead';
import { applyFreeMailFontToBody, applyTemplateFontToBody } from '@/shared/utils/htmlHelpers';
import { defaultMailFont, userChosenFont } from '@/ckeditorConfig';
import PlaceholderModal from './PlaceholderModal';
import AuthService, { functionalities, privileges } from '@/shared/services/AuthService';
import messages from './emailMessages';
import DataCacheService from '@/shared/services/DataCacheService';
import AutoComplete from '@/shared/components/input/select/AutoComplete';

// The signature is only resolved when the mail is sent, so its font cannot be read here.
const freeTextMailContent = () =>
  applyFreeMailFontToBody(
    `<html>
<head>
	<title></title>
</head>
<body style="height: auto; min-height: auto;">
  <p>&nbsp;</p>
  <p>{company.signature}</p>
</body>
</html>`,
    userChosenFont(),
    defaultMailFont(),
  );

class MultiRelationsMailModal extends Component {
  state = {
    emails: [],
    emailAttachments: [],
    templates: [],
    showPlaceholderModal: false,
    autoLinkDocumentTypes: [],
  };

  componentDidMount() {
    this.form = React.createRef();
    const { emailContext } = this.props;
    this.retrieveEmailTemplates(emailContext);
  }

  static propTypes = {
    onSave: PropTypes.func.isRequired,
    intl: intlShape.isRequired,
    onClose: PropTypes.func.isRequired,
    show: PropTypes.bool,
    languages: PropTypes.array.isRequired,
    emailContext: PropTypes.number.isRequired,
  };

  static defaultProps = {
    show: false,
  };

  retrieveEmailTemplates = emailContext =>
    api.base.get(`${config.appUrls.api}/api/template/company/${emailContext}`).then(({ data }) => {
      if (!data.length) {
        this.renderEmailInput();
        return;
      }
      const templates = data.map(x => ({
        id: x.id,
        name: x.name,
      }));
      this.setState({ templates });
    });

  onSave = () => {
    const { emails, emailAttachments, selectedTemplate } = this.state;
    const { onSave, intl } = this.props;

    const isFormValid = this.validate();

    let invalidEmailBody = false;

    emails.map(x => {
      if (
        !x.content ||
        // regex to remove line breaks
        x.content.replace(/(\r\n|\n|\r)/gm, '') === '<p></p>'
      ) {
        invalidEmailBody = true;
      }
    });

    if (invalidEmailBody || !isFormValid) {
      ToastrService.error(intl.formatMessage(messages.SendMailMakeSureAllFieldsAreFilledOut));
      return Promise.reject(new Error('invalid form'));
    }

    const parameters = {
      emailTemplateId: selectedTemplate,
      emails,
      emailAttachments: emailAttachments.map(x => ({
        documentId: x.id,
        languageCode: x.languageCode,
      })),
    };

    return onSave(parameters);
  };

  closeModal = () => {
    const { onClose } = this.props;
    onClose();
  };

  sortByUserLanguage = (a, b) => {
    var userLanguage = DataCacheService.getUserLanguage(AuthService.getUserId());
    if (a.languageCode === userLanguage) {
      return -1;
    }
    return 1;
  };

  getTemplateAttachments = (language, templateLanguageId) =>
    api.base
      .get(`${config.appUrls.documents}/api/Documents/Links/${templateLanguageId}/4`)
      .then(({ data }) => data.map(doc => this.mapAttachment(doc, language)));

  renderEmailInput = () => {
    const { languages } = this.props;

    const content = freeTextMailContent();
    const emails = languages.map((x, index) => ({
      subject: '',
      content,
      initialValue: content,
      documentDate: moment.utc(),
      languageCode: x,
      collapsed: x !== DataCacheService.getUserLanguage(AuthService.getUserId()),
    }));

    this.setState(state => ({
      ...state,
      emails,
    }));
  };

  emailTitleChanged = (email, title) => {
    const { emails } = this.state;

    this.setState({
      emails: emails.map(e => {
        if (e.languageCode === email.languageCode)
          return {
            ...e,
            title,
          };
        return e;
      }),
    });
  };

  emailContentChanged = (email, content) => {
    const { emails } = this.state;

    this.setState({
      emails: emails.map(e => {
        if (e.languageCode === email.languageCode)
          return {
            ...e,
            content,
          };
        return e;
      }),
    });
  };

  onEditorStateChange = (email, content) => {
    const { emails } = this.state;
    this.setState({
      emails: emails.map(e => {
        if (e.languageCode === email.languageCode)
          return {
            ...e,
            content,
          };
        return e;
      }),
    });
  };

  renderSingleEmail = email => {
    const documentUploadEndpoint = `${config.appUrls.documents}/api/documents?documentType=15`;
    const { emailAttachments, selectedTemplate } = this.state;

    return (
      <EmailEditor
        id={email.languageCode}
        isFreeEmail={!selectedTemplate}
        title={email.title}
        onTitleChanged={title => this.emailTitleChanged(email, title)}
        initialValue={email.initialValue}
        onContentChanged={content => this.onEditorStateChange(email, content)}
        onAttachmentUploaded={(error, file) =>
          this.onDocumentUploaded(error, file, email.languageCode)
        }
        attachmentUploadEndpoint={documentUploadEndpoint}
        attachments={emailAttachments.filter(x => x.languageCode === email.languageCode)}
        onDeleteAttachment={this.deleteAttachment}
        documentDate={email.documentDate}
        onDocumentDateChanged={documentDate =>
          this.setState(state => ({
            ...state,
            emails: state.emails.map(x => {
              if (x.languageCode === email.languageCode) {
                return {
                  ...x,
                  documentDate,
                };
              }
              return x;
            }),
          }))
        }
      />
    );
  };

  emailCollapseClicked = email => {
    const { emails } = this.state;

    const emailsCopy = [...emails];

    const item = emailsCopy.find(x => x.languageCode === email.languageCode);

    item.collapsed = !item.collapsed;

    this.setState(state => ({
      ...state,
      emails: emailsCopy,
    }));
  };

  mapAttachment = (document, languageCode) => ({
    ...document,
    languageCode,
  });

  onDocumentUploaded = (error, document, languageCode) => {
    const { intl } = this.props;

    if (error) {
      log.error(error);
      ToastrService.error(intl.formatMessage(messages.MsgEmailAttachmentUploadFailed));
      return;
    }

    const emailAttachment = this.mapAttachment(document, languageCode);

    this.setState(state => ({
      ...state,
      emailAttachments: [emailAttachment, ...state.emailAttachments],
    }));
  };

  deleteAttachment = attachment => {
    if (
      AuthService.hasPermission({
        functionality: functionalities.Documents,
        privilege: privileges.Delete,
      }) &&
      attachment.documentType === 15
    ) {
      api.base.delete(`${config.appUrls.documents}/api/documents/${attachment.id}`).then(() => {
        this.setState(state => ({
          emailAttachments: state.emailAttachments.filter(x => x.id !== attachment.id),
        }));
      });
    } else {
      this.setState(state => ({
        emailAttachments: state.emailAttachments.filter(x => x.id !== attachment.id),
      }));
    }
  };

  renderMultipleEmails = emails => {
    const { intl } = this.props;
    return emails.sort(this.sortByUserLanguage).map(email => (
      <CollapsablePanel
        key={email.languageCode}
        collapsed={email.collapsed}
        collapseChanged={() => this.emailCollapseClicked(email)}
        title={
          <span className="margin10L">
            {intl.formatMessage(listItemMsg('language', email.languageCode, email.languageCode))}
          </span>
        }
      >
        <div>{this.renderSingleEmail(email)}</div>
      </CollapsablePanel>
    ));
  };

  loadEmailTemplates = (templateId, languageCodes) => {
    const { intl } = this.props;

    return Promise.all(
      languageCodes.map((languageCode, index) =>
        api.base
          .get(`${config.appUrls.api}/api/template/content/${templateId}/${languageCode}`)
          .then(({ data }) => {
            const content = applyTemplateFontToBody(data.content);
            return {
              ...data,
              content,
              initialValue: content,
              collapsed: languageCode !== DataCacheService.getUserLanguage(AuthService.getUserId()),
              documentDate: moment.utc(),
            };
          })
          // if no template was found in the language code, just return an empty object to
          // still be able to use the editor
          .catch(() => {
            const language = intl.formatMessage(
              listItemMsg('language', languageCode, languageCode),
            );
            const message = intl.formatMessage(messages.NoEmailTemplateFoundInLanguage, {
              language,
            });
            ToastrService.error(message);
            const content = freeTextMailContent();
            return {
              languageCode,
              initialValue: content,
              content,
              title: '',
              collapsed: languageCode !== DataCacheService.getUserLanguage(AuthService.getUserId()),
              documentDate: moment.utc(),
            };
          }),
      ),
    );
  };

  templateChanged = item => {
    const template = item?.key;
    this.setState(state => ({
      ...state,
      selectedTemplate: template,
    }));

    if (!template) return;

    const { languages } = this.props;

    this.loadEmailTemplates(template, languages).then(
      emailTemplates => {
        this.setState(state => ({
          ...state,
          emails: emailTemplates,
          loadingTemplate: false,
          autoLinkDocumentTypes: emailTemplates[0].autoLinkDocumentTypes,
        }));
        Promise.all(
          emailTemplates
            .filter(x => !!x.id)
            .map(x => this.getTemplateAttachments(x.languageCode, x.id)),
        ).then(([...results]) => {
          this.setState(state => ({
            emailAttachments: [...results.flat()],
          }));
        });
      },
      error => {
        console.error(error);
        this.setState(state => ({
          ...state,
          loadingTemplate: false,
        }));
      },
    );
  };

  validate() {
    const { emails } = this.state;
    if (!emails.length) return false;
    if (!this.form || !this.form.current) return false;

    return this.form.current.reportValidity();
  }

  render() {
    const { show, intl, stateService, emailContext } = this.props;
    const {
      emails,
      templates,
      selectedTemplate,
      showPlaceholderModal,
      autoLinkDocumentTypes,
    } = this.state;
    return (
      <OkCancelModal
        title={intl.formatMessage(messages.SendEmail)}
        show={show}
        onClose={this.closeModal}
        onSave={this.onSave}
        size="lg"
        leftButton={
          <Button onClick={() => this.setState({ showPlaceholderModal: true })}>
            {'{'}
            {intl.formatMessage(messages.Placeholders)}
            {'}'}
          </Button>
        }
      >
        {showPlaceholderModal && (
          <PlaceholderModal
            onSave={() => this.setState({ showPlaceholderModal: false })}
            onClose={() => this.setState({ showPlaceholderModal: false })}
            templateContext={emailContext}
          />
        )}

        {show && (
          <form ref={this.form}>
            <div className="form-horizontal">
              <div className="form-group">
                <label className="control-label col-sm-3">
                  {intl.formatMessage(messages.Template)}
                </label>
                <div className="col-md-6">
                  {!templates.length && (
                    <label className="control-label">
                      {intl.formatMessage(messages.NoTemplatesFound)}
                    </label>
                  )}
                  {templates.length > 0 && (
                    <AutoComplete
                      items={templates
                        .map(template => ({
                          key: template.id,
                          label: intl.formatMessage(
                            listItemMsg('template', template.id, template.name),
                          ),
                        }))
                        .sort((a, b) => a.label.toLowerCase().localeCompare(b.label.toLowerCase()))}
                      initialSelectedItem={
                        selectedTemplate
                          ? templates
                              .map(template => ({
                                key: template.id,
                                label: intl.formatMessage(
                                  listItemMsg('template', template.id, template.name),
                                ),
                              }))
                              .find(item => item.key === selectedTemplate)
                          : undefined
                      }
                      placeholder={intl.formatMessage(messages.ChooseTemplate)}
                      mode="edit"
                      onChange={this.templateChanged}
                      onClear={() => this.templateChanged(null)}
                    />
                  )}
                </div>
                <span className="col-md-2">
                  <CanRead on="settings">
                    <button
                      type="button"
                      className="btn btn-default"
                      title={intl.formatMessage(messages.OpenEmailTemplates)}
                      onClick={() => stateService.goToEmailTemplates()}
                    >
                      <LinkIcon className="margin5R" />
                      <FormattedMessage {...messages.Templates} />
                    </button>
                  </CanRead>
                </span>
              </div>

              {emails && emails.length > 0 && (
                <div className="form-group padding10T">
                  <span className="bold col-sm-offset-3 col-md-8">
                    <span>
                      {intl.formatMessage(messages.PlaceholdersWillBeResolvedByAdminPulse)}
                    </span>
                  </span>
                </div>
              )}
              {emails &&
                emails.length > 0 &&
                autoLinkDocumentTypes &&
                autoLinkDocumentTypes.length > 0 && (
                  <div className="form-group">
                    <span className=" col-sm-offset-3 col-md-8">
                      <span className="bold">
                        {intl.formatMessage(
                          messages.DocumentsOfTheFollowingTypeWillBeLinkedIfPresent,
                        )}
                      </span>

                      <ul>
                        {autoLinkDocumentTypes.map(x => (
                          <li>{intl.formatMessage(listItemMsg('documentType', x, x))}</li>
                        ))}
                      </ul>
                    </span>
                  </div>
                )}
              {templates.length >= 1 && (!emails || emails.length <= 0) && (
                <div className="form-group padding10T">
                  <span
                    className="col-sm-offset-3 col-md-8 text-clickable"
                    id="MultiRelationModal-ClickHereToCreateFreeTextEmail"
                    onClick={this.renderEmailInput}
                  >
                    {intl.formatMessage(messages.ClickHereToCreateFreeTextEmail)}
                  </span>
                </div>
              )}

              {emails &&
                (emails.length === 1
                  ? this.renderSingleEmail(emails[0])
                  : this.renderMultipleEmails(emails))}
            </div>
          </form>
        )}
      </OkCancelModal>
    );
  }
}

export default injectStateService(MultiRelationsMailModal);
